import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { Store } from '@ngrx/store';

import { AvatarComponent } from '@shared/ui/data-display/avatar/avatar.component';
import { ModalComponent } from '@shared/ui/overlay/modal/modal.component';

import { AuthService } from '@core/auth/auth.service';
import { AppNotification } from '@core/models';
import { hasResourceAccess, resourceForRoute, realmRoleOf } from '@core/util/access-control.util';
import { pendingApprovalCount } from '@core/util/time-approvals.util';
import { onboardingProgress } from '@core/util/onboarding-progress.util';
import { onboardingAssignmentsFor } from '@core/util/onboarding-assignments.util';
import { outstandingTrainingCount, supervisorAttentionCount } from '@core/util/training-access.util';
import { MeApiService } from '@core/data/api/me-api.service';
import { ScheduledHoursService } from '@core/util/scheduled-hours.service';
import { OnboardingPageActions } from '@state/onboarding/onboarding.actions';
import { selectDocs, selectNewHires } from '@state/onboarding/onboarding.reducer';
import { PeoplePageActions } from '@state/people/people.actions';
import { TrainingPageActions } from '@state/training/training.actions';
import {
  selectAssignedCategories,
  selectAssignments,
  selectCompletions,
  selectFramework,
  selectLevels,
} from '@state/training/training.reducer';
import { NotificationsActions } from '@state/notifications/notifications.actions';
import { selectNotifications } from '@state/notifications/notifications.reducer';
import { ProjectsPageActions } from '@state/projects/projects.actions';
import { UsersAccessPageActions } from '@state/users-access/users-access.actions';
import { selectAccessMatrix } from '@state/users-access/users-access.reducer';
import { selectAllProjects } from '@state/projects/projects.reducer';
import { ReferenceActions } from '@state/reference/reference.actions';
import { selectPersonas } from '@state/reference/reference.reducer';
import { SessionActions } from '@state/session/session.actions';
import { selectAccessStale, selectCurrentPersona } from '@state/session/session.reducer';
import { todayIso } from '@core/util/date.util';

/**
 * How far a screen has got in being wired to the backend.
 *
 * A build-time note, not app data: it tells whoever is using the prototype which
 * pages are live against the API, which are mid-wiring, and which are still
 * running on seeded data. Delete the map in `WIRING` when every page is done.
 *
 * **Three states, and the distinction between the first two is the point:**
 *
 * | | dot | means |
 * |---|---|---|
 * | `tested` | blue | built AND exercised against the server |
 * | `done` | green | built, not yet exercised |
 * | `wip` | yellow | being worked on, not finished |
 *
 * `done` used to mean both of the first two at once, which made the mark
 * useless for the only question anybody asks of it: *has this actually been
 * tried?* Written code and working code look identical from here.
 */
export type WiringStatus = 'tested' | 'done' | 'wip';

/**
 * Backend-wiring status by route. Absent = not started.
 *
 * **Keep this honest.** `tested` is a claim that somebody ran it against a real
 * server and watched it work — not that the code compiles and looks right. When
 * in doubt a screen is `done`, which costs nothing; a wrong `tested` is how a
 * broken screen gets shipped on the strength of a green dot.
 */
const WIRING: Record<string, WiringStatus> = {
  // Built out this week: volumes, locations, pay classes, employment types, the
  // equipment and software catalogues, and their inventory. Not yet exercised
  // end to end against a server, so green rather than blue.
  '/company': 'done',
  '/schedule': 'tested',
  '/people': 'wip',
  // Green on Julie's word, 21 October 2026: built and IN TESTING — the library,
  // authoring, checklists and document import read and write against the
  // server. Blue (`tested`) once that testing is done.
  '/business-model': 'done',
  // Reads and writes: the catalogue, authoring, assignment, what somebody owes on
  // a given day, and ticking it. `wip` rather than `done` because a tick cannot be
  // taken back and the period view stops at a month — docs/backend-request-checklists.md.
  '/checklists': 'wip',
  // Reads and writes both: sections, editing one, publishing it, and its files.
  // `wip` rather than `done` only because three admin controls are disabled
  // waiting on endpoints — docs/backend-request-handbook.md.
  '/handbook': 'wip',
  // Reads only so far: the catalogue, each process in full, phase content,
  // responsibilities, training and files all come from the server; authoring is
  // still session-only.
  '/operating-systems': 'wip',
  // Reads only: positions, job descriptions, policies, procedures and seat
  // requirements come from the server, authoring is still session-only. That is
  // `wip`, not `done` — `done` means writes have been exercised too.
  '/org-chart': 'wip',
  // Yellow on Julie's word, 21 October 2026. Reads the pathways and who stands
  // where, and writes a pathway's details, and its ladder with the positions on
  // it in one call, against the server. `wip` until it has been tested there.
  '/career-pathways': 'wip',
  // Yellow on Julie's word, 21 October 2026. Reads the framework and writes
  // modules, categories, competencies, levels, tiers, flags and assignments, one
  // call each. `wip`: untested against the server, CSV import and module links
  // wait on docs/backend-request-training-wiring.md, and My Training is next.
  '/training': 'wip',
  // Yellow on Julie's word, 24 October 2026. The packet's tabs read the RPM Plan,
  // the checklists' due reads (the shared Today / Week / Month / Year scorecard,
  // by checklist too) and the access matrix; it writes nothing of its own.
  // `wip` until it has been tested against the server.
  '/mrp': 'wip',
  // Green on Julie's word, 23 October 2026: built and IN TESTING. The running
  // list, week history, weekly review, categories, Grass Catcher and promote all
  // read and write against the server (docs/backend-spec-rpm-plan.md). Blue
  // (`tested`) once that testing is done.
  '/rpm': 'done',
};

/** A rendered navigation link. `route` is null while the screen is not yet ported. */
interface NavItem {
  readonly label: string;
  readonly icon: string;
  readonly route: string | null;
  /** Optional query params for the link (e.g. deep-linking a page's tab). */
  readonly query?: Record<string, string>;
  /** Attention count mirrored from a badge on one of the page's tabs (0 = none). */
  readonly badge?: number;
  /** Backend-wiring status, resolved from WIRING by route. */
  readonly wiring?: WiringStatus;
}

/** A titled group of nav links. An empty title renders an untitled top group. */
interface NavSection {
  readonly title: string;
  readonly items: readonly NavItem[];
}

/** One "View As" chip in the top bar persona switcher. */
interface PersonaChip {
  readonly id: string;
  readonly name: string;
  readonly tier: string;
}

/** Internal section descriptor: a NavItem plus its persona-based visibility. */
interface NavDescriptor extends NavItem {
  readonly show: boolean;
}

/**
 * Application shell: a navy top bar (logo, wordmark, "View As" persona switcher,
 * account avatar) over a white, role-aware sidebar and the routed content area.
 *
 * The sidebar mirrors the prototype's `Sidebar` exactly: the same sections
 * (untitled top group, the current persona's first name, Projects, Diamond Ridge,
 * Admin), labels, order, and persona-based visibility. Only screens visible in the
 * prototype appear; ported screens link to their route, the rest show a "soon" tag.
 */
@Component({
  selector: 'dros-shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, AvatarComponent, ModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './shell.component.html',
})
export class ShellComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly auth = inject(AuthService);
  /** Their roles were rewritten and their token predates it — see the banner. */
  protected readonly accessStale = this.store.selectSignal(selectAccessStale);

  private readonly personas = this.store.selectSignal(selectPersonas);
  protected readonly currentPersona = this.store.selectSignal(selectCurrentPersona);
  private readonly accessMatrix = this.store.selectSignal(selectAccessMatrix);
  private readonly projects = this.store.selectSignal(selectAllProjects);
  private readonly newHires = this.store.selectSignal(selectNewHires);
  private readonly onboardingDocs = this.store.selectSignal(selectDocs);
  // The training slice and the persona's scheduled hours feed the "My Training" due-today badge.
  private readonly trainingFramework = this.store.selectSignal(selectFramework);
  private readonly trainingAssignments = this.store.selectSignal(selectAssignments);
  private readonly trainingAssignedCategories = this.store.selectSignal(selectAssignedCategories);
  private readonly trainingCompletions = this.store.selectSignal(selectCompletions);
  private readonly trainingLevels = this.store.selectSignal(selectLevels);
  /** The persona's scheduled hours — whether today is a workday, for daily training. */
  private readonly scheduledHours = inject(ScheduledHoursService);
  /** What the server says this caller may see; null until it answers, or with no API. */
  private readonly meApi = inject(MeApiService);
  private readonly todayISO = todayIso();
  /** Every in-app notification in the store, newest first (all recipients). */
  private readonly notifications = this.store.selectSignal(selectNotifications);

  /** Whether the top-bar notifications dropdown panel is currently open. */
  protected readonly bellOpen = signal(false);

  /**
   * Just the current persona's notifications, newest first. The store holds every
   * recipient's notifications, so we filter to the person being "viewed as" — the
   * same person the badge counts and the panel lists.
   */
  protected readonly myNotifications = computed<readonly AppNotification[]>(() => {
    const id = this.currentPersona()?.id;
    return id ? this.notifications().filter((n) => n.recipientId === id) : [];
  });

  /** Unread notifications for the current persona — the number shown on the bell badge. */
  protected readonly unreadCount = computed<number>(() => this.myNotifications().filter((n) => !n.read).length);

  /** The handful of most-recent notifications shown in the bell modal (full list lives on /alerts). */
  protected readonly recentNotifications = computed<readonly AppNotification[]>(() => this.myNotifications().slice(0, 6));

  /**
   * Undismissed notifications for the current persona — the badge on the Today nav
   * item. These are exactly the banners showing on the Today page, so the badge and
   * the page agree ("something happened, and it's still on your Today").
   */
  protected readonly todayAlertCount = computed<number>(() => this.myNotifications().filter((n) => !n.dismissed).length);

  /**
   * Crew time entries awaiting the current persona's review — the count shown on
   * the My Projects "Team Time" tab, mirrored onto the sidebar's My Projects item.
   * Uses the shared approvals util so it matches the Today tile and approve tab.
   */
  protected readonly pendingApprovals = computed<number>(() =>
    pendingApprovalCount(this.currentPersona(), this.projects(), this.personas()),
  );

  /**
   * The signed-in persona is enrolled in ONBOARDING — the module, meaning the
   * new-hire list has a record for them.
   *
   * **Not their account status.** It used to read `persona.inOnboarding`, which
   * was `accountStatus === PENDING_ONBOARDING` — "invited, password never set" —
   * so anybody who had not signed in yet was shown an Onboarding item leading to
   * a page with nothing on it, while somebody genuinely mid-onboarding who had
   * set their password was shown none.
   */
  protected readonly enrolledInOnboarding = computed<boolean>(() => {
    const id = this.currentPersona()?.id;
    return !!id && this.newHires().some((h) => h.id === id);
  });

  /**
   * Items the current persona still has to complete in onboarding — unsigned
   * documents + incomplete checklist tasks. Shown as a badge on the sidebar's
   * Onboarding item. Uses the shared onboarding-progress util so it matches the
   * My Onboarding page exactly.
   */
  protected readonly onboardingRemaining = computed<number>(() => {
    const persona = this.currentPersona();
    if (!persona || !this.enrolledInOnboarding()) return 0;
    const hire = this.newHires().find((h) => h.id === persona.id);
    return onboardingProgress(hire, this.onboardingDocs()).remaining;
  });

  /** Onboarding steps assigned to the current persona (as role holder / supervisor
   *  / final reviewer) — badged on the People item so they know to act. */
  protected readonly onboardingAssignedToMe = computed<number>(() =>
    onboardingAssignmentsFor(this.currentPersona()?.id ?? '', this.newHires()).length,
  );

  /**
   * Training competencies the current persona still needs to do today — the badge on
   * the My Training item. Uses the shared count util so it matches the page (mastered,
   * N/A, as-needed, and day-off daily items are excluded; only not-done-this-period counts).
   */
  protected readonly dueTrainingCount = computed<number>(() => {
    const persona = this.currentPersona();
    if (!persona) return 0;
    const ids = this.trainingAssignments()[persona.id] ?? [];
    const modules = this.trainingFramework().filter((m) => ids.includes(m.id));
    if (!modules.length) return 0;
    return outstandingTrainingCount(
      modules,
      persona.id,
      // Per-persona records (keyed by competency id) — NOT the whole persona→records map,
      // or nothing would match and every competency would count as due.
      this.trainingCompletions()[persona.id] ?? {},
      this.trainingAssignedCategories(),
      this.trainingLevels(),
      this.todayISO,
      // Their own scheduled hours decide it — no hours set, no workday, so no
      // daily item is due (matches the backend's WorkdayCalendar).
      this.scheduledHours.isWorkday(persona.id, this.todayISO),
    );
  });

  /**
   * How many of the current persona's direct reports need something from them —
   * the badge on the "My Team (New)" nav item. A report counts once if they have
   * ANY competency awaiting sign-off or coming due for review; the per-item total
   * lives on each roster chip. Uses the shared supervisorAttentionCount so the nav
   * badge, the roster badges, and the review worklist all agree.
   */
  protected readonly teamAttentionCount = computed<number>(() => {
    const persona = this.currentPersona();
    const reportIds = persona?.directReports ?? [];
    if (!reportIds.length) return 0;
    const framework = this.trainingFramework();
    const assignments = this.trainingAssignments();
    const assignedCategories = this.trainingAssignedCategories();
    const completions = this.trainingCompletions();
    const levels = this.trainingLevels();
    let people = 0;
    for (const id of reportIds) {
      const modules = framework.filter((m) => (assignments[id] ?? []).includes(m.id));
      const count = supervisorAttentionCount(
        modules,
        id,
        completions[id] ?? {},
        assignedCategories,
        levels,
        this.todayISO,
      );
      if (count > 0) people++;
    }
    return people;
  });

  /**
   * Whether the "View As" switcher shows: ADMINISTRATORS only (Julie, 26 October
   * 2026). Decided by the SIGNED-IN user's Keycloak role, never by the persona
   * being viewed — a check on the viewed persona would hide the strip the moment
   * an admin switched to somebody else, leaving no way back. Everybody else sees
   * only their own record, which the session resolves from who signed in.
   */
  protected readonly canViewAs = computed(() => this.auth.hasAnyRole([realmRoleOf('admin')]));

  /** Personas offered in the top-bar "View As" switcher. */
  protected readonly personaChips = computed<PersonaChip[]>(() =>
    this.personas().map((p) => ({ id: p.id, name: p.name, tier: p.tier })),
  );


  constructor() {
    // The current persona's scheduled hours, for the due-today badge. An effect
    // because the persona can arrive after the shell, and changes on "View As";
    // the shared read fetches each person's record once.
    effect(() => this.scheduledHours.request(this.currentPersona()?.id));
  }

  ngOnInit(): void {
    // Load global reference data (people, operating systems) once at app start.
    this.store.dispatch(ReferenceActions.load());
    // Projects feed the sidebar's Team Time approval badge.
    this.store.dispatch(ProjectsPageActions.entered());
    // The access matrix gates the sidebar (and the route guard), so load it here.
    this.store.dispatch(UsersAccessPageActions.entered());
    // New-hire onboarding data feeds the Onboarding sidebar badge before the page is opened.
    this.store.dispatch(OnboardingPageActions.entered());
    // Training feeds the "My Training" due-today badge. The people roster is
    // loaded app-wide here because the reference personas are built from it.
    this.store.dispatch(TrainingPageActions.entered());
    this.store.dispatch(PeoplePageActions.entered());

    // Who is actually signed in. Dispatched here, once, rather than from the route
    // guard, which runs on EVERY navigation and would stamp on a "view as" switch
    // each time. `/me` is cached by the service, so this does not re-request.
    void this.meApi.current().then((caller) => {
      if (caller?.personaCode) {
        this.store.dispatch(
          SessionActions.signedInPersonResolved({ personaCode: caller.personaCode }),
        );
      }
    });
  }

  /**
   * A nav item is shown when its data precondition holds AND the current
   * persona's role has access to the mapped resource (admin/unmapped = allowed).
   */
  private allow(route: string, precond = true): boolean {
    return (
      precond &&
      hasResourceAccess(this.accessMatrix(), this.currentPersona(), resourceForRoute(route), {
        roles: this.meApi.roles(),
        levels: this.meApi.levels(),
      })
    );
  }

  protected selectPersona(personaId: string): void {
    this.store.dispatch(SessionActions.personaSelected({ personaId }));
  }

  /**
   * Toggle the notifications panel. Opening it marks all of the current persona's
   * notifications read (so the bell badge clears the moment they look), mirroring
   * the reducer's `readAll` behaviour. Closing it is a no-op beyond hiding the panel.
   */
  protected toggleBell(): void {
    const opening = !this.bellOpen();
    this.bellOpen.set(opening);
    const recipientId = this.currentPersona()?.id;
    if (opening && recipientId) {
      this.store.dispatch(NotificationsActions.readAll({ recipientId }));
    }
  }

  /** Close the notifications panel — used by the backdrop click and by row navigation. */
  protected closeBell(): void {
    this.bellOpen.set(false);
  }

  /**
   * A compact, human "time ago" for a notification's `createdAt` ISO timestamp
   * (e.g. "just now", "5m ago", "3h ago", "2d ago", "1w ago"). Kept local to the
   * bell because it is the only consumer; promote to date.util if reused elsewhere.
   */
  protected shortTime(iso: string): string {
    const elapsedMs = Math.max(0, Date.now() - new Date(iso).getTime());
    const minutes = Math.floor(elapsedMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  }

  /** End the real Keycloak session (returns to login). */
  protected logout(): void {
    void this.auth.logout();
  }

  /**
   * The sidebar, rebuilt for the current persona. Visibility conditions match the
   * prototype `Sidebar` (`show:` flags); labels, icons, and order do too.
   */
  /**
   * The dot's colour for a wiring state.
   *
   * One place, so the legend on Get Started and the dots in the nav cannot say
   * different things about the same state.
   */
  protected wiringDotClass(status: WiringStatus): string {
    if (status === 'tested') return 'bg-sky-500';
    if (status === 'done') return 'bg-emerald-500';
    return 'bg-amber-400';
  }

  /** What hovering the dot says. */
  protected wiringTitle(status: WiringStatus): string {
    if (status === 'tested') return 'Built and tested against the server';
    if (status === 'done') return 'Built, not tested yet';
    return 'Being worked on';
  }

  protected readonly sections = computed<NavSection[]>(() => {
    const persona = this.currentPersona();
    if (!persona) return [];

    const firstName = (persona.name.split(' ')[0] || 'Me').toUpperCase();

    const top: NavDescriptor[] = [
      { label: 'Get Started', icon: 'pi-compass', route: '/get-started', show: this.allow('/get-started') },
      { label: 'Today', icon: 'pi-home', route: '/today', show: this.allow('/today'), badge: this.todayAlertCount() },
      { label: 'Command Center', icon: 'pi-chart-bar', route: '/command', show: this.allow('/command') },
    ];

    const me: NavDescriptor[] = [
      { label: 'Onboarding', icon: 'pi-check-square', route: '/onboarding-me', show: this.allow('/onboarding-me', this.enrolledInOnboarding()), badge: this.onboardingRemaining() },
      { label: 'My Packet', icon: 'pi-file', route: '/packet', show: this.allow('/packet') },
      { label: 'RPM Plan', icon: 'pi-flag', route: '/rpm', show: this.allow('/rpm') },
      { label: 'Checklists', icon: 'pi-list', route: '/checklists', show: this.allow('/checklists') },
      { label: 'Weekly Schedule', icon: 'pi-clock', route: '/schedule', show: this.allow('/schedule') },
      { label: 'MRP', icon: 'pi-clipboard', route: '/mrp', show: this.allow('/mrp') },
      { label: 'My Career Pathway', icon: 'pi-chart-line', route: '/my-pathway', show: this.allow('/my-pathway', persona.hasCareerLadder) },
      { label: 'My Training', icon: 'pi-graduation-cap', route: '/my-training', show: this.allow('/my-training'), badge: this.dueTrainingCount() },
      { label: 'My Projects', icon: 'pi-briefcase', route: '/my-projects', show: this.allow('/my-projects'), badge: this.pendingApprovals() },
      { label: 'Time Tracking', icon: 'pi-clock', route: '/time', show: this.allow('/time') },
      { label: 'My Team', icon: 'pi-users', route: '/team', show: this.allow('/team', (persona.directReports?.length ?? 0) > 0), badge: this.teamAttentionCount() },
    ];

    const projects: NavDescriptor[] = [
      { label: 'Opportunities', icon: 'pi-filter', route: '/opportunities', show: this.allow('/opportunities') },
      { label: 'Scheduling', icon: 'pi-calendar', route: '/proj-scheduling', show: this.allow('/proj-scheduling') },
      { label: 'Project Management', icon: 'pi-th-large', route: '/projects', show: this.allow('/projects') },
      { label: 'Financials', icon: 'pi-dollar', route: '/project-financials', show: this.allow('/project-financials') },
      { label: 'Project Settings', icon: 'pi-sliders-h', route: '/project-settings', show: this.allow('/project-settings') },
    ];

    const diamondRidge: NavDescriptor[] = [
      { label: 'Company', icon: 'pi-building', route: '/company', show: this.allow('/company') },
      { label: 'Business Model', icon: 'pi-file-edit', route: '/business-model', show: this.allow('/business-model') },
      { label: 'Org Chart', icon: 'pi-sitemap', route: '/org-chart', show: this.allow('/org-chart') },
      { label: 'People', icon: 'pi-users', route: '/people', show: this.allow('/people'), badge: this.onboardingAssignedToMe() },
      { label: 'Operating Systems', icon: 'gem', route: '/operating-systems', show: this.allow('/operating-systems') },
      { label: 'Career Pathways', icon: 'pi-chart-line', route: '/career-pathways', show: this.allow('/career-pathways') },
      { label: 'Training Modules', icon: 'pi-graduation-cap', route: '/training', show: this.allow('/training') },
      { label: 'Handbook', icon: 'pi-book', route: '/handbook', show: this.allow('/handbook') },
    ];

    const admin: NavDescriptor[] = [
      { label: 'Users & Access', icon: 'pi-lock', route: '/users', show: this.allow('/users') },
      { label: 'Data Management', icon: 'pi-database', route: '/data', show: this.allow('/data') },
      {
        label: 'Awaiting Assignment',
        icon: 'pi-box',
        route: '/awaiting-assignment',
        show: this.allow('/awaiting-assignment'),
      },
      { label: 'Release Notes', icon: 'pi-history', route: '/release-notes', show: this.allow('/release-notes') },
    ];

    return [
      this.toSection('', top),
      this.toSection(firstName, me),
      this.toSection('Projects', projects),
      this.toSection('Diamond Ridge', diamondRidge),
      this.toSection('Admin', admin),
    ].filter((section) => section.items.length > 0);
  });

  /** Drop hidden descriptors and strip the `show` flag down to a NavItem. */
  private toSection(title: string, descriptors: readonly NavDescriptor[]): NavSection {
    return {
      title,
      items: descriptors
        .filter((descriptor) => descriptor.show)
        // Wiring status is attached here, from the route, so the descriptors above
        // stay about navigation and one map says what is wired.
        .map(({ label, icon, route, query, badge }) => ({
          label,
          icon,
          route,
          query,
          badge,
          wiring: route ? WIRING[route] : undefined,
        })),
    };
  }
}
